import React, { useState } from 'react';
import { ClipboardText, CarProfile, CarSimple, Jeep, Lightning } from '@phosphor-icons/react';
import { api } from '../api';
import { useCarModels } from '../useCarModels';
import { showToast } from '../components/Toast';
import { BrandTag } from '../components/Badges';

const BRANDS = ['KIA', 'SUZUKI', 'GWM', 'GAC'];

export default function CarModels() {
  const { models, grouped, loading, refresh } = useCarModels();
  const [newBrand, setNewBrand] = useState('KIA');
  const [newModel, setNewModel] = useState('');
  const [saving, setSaving]     = useState(false);

  async function add(e) {
    e.preventDefault();
    if (saving) return;
    const name = newModel.trim();
    if (!name) return showToast('Type a model name');
    setSaving(true);
    try {
      await api.addCarModel({ brand: newBrand, model: name });
      showToast(`${newBrand} · ${name} added`);
      setNewModel('');
      await refresh();
    } catch (e) {
      showToast('Failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function remove(m) {
    if (!window.confirm(`Delete model "${m.brand} ${m.model}"?\n\nVehicles already created with this model will keep their model name — this only removes it from the dropdown.`)) return;
    try {
      await api.deleteCarModel(m.id);
      showToast(`${m.brand} · ${m.model} removed`);
      await refresh();
    } catch (e) {
      showToast('Failed: ' + e.message);
    }
  }

  if (loading) return <div className="loading">Loading car models…</div>;

  const counts = { total: (models || []).length };
  BRANDS.forEach((b) => { counts[b] = (grouped[b] || []).length; });

  return (
    <div>
      <div className="ph">
        <div>
          <div className="pt">Car Models</div>
          <div className="ps">Manage the model dropdown for KIA · SUZUKI · GWM (Manager only)</div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats">
        <StatCard icon={ClipboardText} cls="bl" value={counts.total}  label="Total" />
        <StatCard icon={CarProfile}    cls="r"  value={counts.KIA}    label="KIA" />
        <StatCard icon={CarSimple}     cls="bl" value={counts.SUZUKI} label="SUZUKI" />
        <StatCard icon={Jeep}          cls="am" value={counts.GWM}    label="GWM" />
        <StatCard icon={Lightning}     cls="te" value={counts.GAC}    label="GAC" />
      </div>

      {/* Add form */}
      <div className="af" style={{ maxWidth: 500, marginBottom: 24 }}>
        <div className="sec" style={{ marginBottom: 10 }}>➕ Add new model</div>
        <form onSubmit={add}>
          <div className="fg2">
            <div>
              <label className="afl">Brand</label>
              <select className="afi" value={newBrand} onChange={(e) => setNewBrand(e.target.value)}>
                {BRANDS.map((b) => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="afl">Model name</label>
              <input
                className="afi"
                placeholder="e.g. Seltos"
                value={newModel}
                onChange={(e) => setNewModel(e.target.value)}
              />
            </div>
          </div>
          <button type="submit" className="btn-in" disabled={saving}>
            {saving ? 'Adding…' : '+ Add Model'}
          </button>
        </form>
      </div>

      {/* Lists per brand */}
      {BRANDS.map((brand) => (
        <BrandSection key={brand} brand={brand} models={(models || []).filter((m) => m.brand === brand)} onRemove={remove} />
      ))}
    </div>
  );
}

function BrandSection({ brand, models, onRemove }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <BrandTag brand={brand} />
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>
          {models.length} model{models.length === 1 ? '' : 's'}
        </span>
      </div>
      {models.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>No models yet for {brand}.</div>
      ) : (
        <div className="tw">
          <table className="vt">
            <thead>
              <tr>
                <th>Model</th>
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {models.map((m) => (
                <tr key={m.id} className="row-even">
                  <td><div className="vm">{m.model}</div></td>
                  <td><button className="ab r" onClick={() => onRemove(m)}>Delete</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, cls, value, label }) {
  return (
    <div className="sc">
      <div className={`sc-icon ${cls}`}><Icon size={20} weight="duotone" /></div>
      <div className="sc-data">
        <div className="sn">{value}</div>
        <div className="sl">{label}</div>
      </div>
    </div>
  );
}
